<template>
  <div
    v-if="filteredModules && filteredModules.length > 0"
    class="custom-modules-section"
  >
    <div
      class="custom-module"
      v-for="module in filteredModules"
      :key="module.id"
    >
      <!-- 模块标题 -->
      <div class="module-header">
        <h3 class="module-title">{{ module.module_title }}</h3>
      </div>

      <!-- 模块内容 -->
      <div class="module-content" :class="getContentClass(module)">
        <div
          v-for="(article, index) in module.article_info"
          :key="index"
          class="article-item"
          :class="getItemClass(module)"
          @click="handleArticleClick(article)"
        >
          <!-- 图片 -->
          <div class="article-image">
            <img
              :src="article.img_url"
              :alt="article.article_title"
              @error="handleImageError"
            />
          </div>

          <!-- module_type 4: 左图右双文案 -->
          <div
            v-if="
              module.module_type === ModuleType.LEFT_IMAGE_RIGHT_DOUBLE_TEXT
            "
            class="article-text-wrapper"
          >
            <div class="article-title" v-if="article.article_title">
              {{ article.article_title }}
            </div>
            <div class="article-subtitle" v-if="article.sub_title">
              {{ article.sub_title }}
            </div>
          </div>

          <!-- module_type 1 或 2: 上图下文 -->
          <div v-else-if="article.article_title" class="article-title">
            {{ article.article_title }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import sdkBridge from "@/utils/sdkBridge";
import { getGameConfig } from "@/config/gameConfig";
import { useForumStore, useLanguageStore } from "@/stores";

interface ArticleInfo {
  img_url: string;
  article_title: string;
  article_url: string;
  sub_title?: string;
  jump_type?: number;
  page_id?: string;
}

interface CustomModule {
  id: string;
  game_id: string;
  fid: string;
  module_title: string;
  module_type: string;
  weight: string;
  article_info: ArticleInfo[];
}

enum ModuleType {
  SHORT_IMAGE_SINGLE_TEXT = "1", // 短图单文案
  LONG_IMAGE_SINGLE_TEXT = "2", // 长图单文案
  QUICK_ACCESS = "3", // 金刚区
  LEFT_IMAGE_RIGHT_DOUBLE_TEXT = "4", // 左图右双文案
}

const forumStore = useForumStore();
const languageStore = useLanguageStore();
const router = useRouter();

// 过滤出 module_type 为 1、2、4 的模块
const filteredModules = computed(() => {
  if (!forumStore.customModules) return [];
  return forumStore.customModules.filter(
    (module) =>
      module.module_type === ModuleType.SHORT_IMAGE_SINGLE_TEXT ||
      module.module_type === ModuleType.LONG_IMAGE_SINGLE_TEXT ||
      module.module_type === ModuleType.LEFT_IMAGE_RIGHT_DOUBLE_TEXT
  );
});

// 根据module_type计算样式类
const getContentClass = (module: CustomModule) => {
  const baseClass: Record<string, boolean> = {
    "layout-type-2": module.module_type === ModuleType.LONG_IMAGE_SINGLE_TEXT, // 长图单文案 每项占一行
  };

  // type4（左图右双文案）无论奇偶数都使用网格布局（一行两个）
  if (module.module_type === ModuleType.LEFT_IMAGE_RIGHT_DOUBLE_TEXT) {
    baseClass["layout-type-1"] = true;
  }

  // type1（短图单文案）根据奇偶数选择布局
  if (module.module_type === ModuleType.SHORT_IMAGE_SINGLE_TEXT) {
    if (module.article_info.length % 2 === 0) {
      baseClass["layout-type-1"] = true;
    } else {
      baseClass["layout-type-1-odd"] = true;
    }
  }

  return baseClass;
};

const getItemClass = (module: CustomModule) => {
  return {
    "item-type-1": module.module_type === ModuleType.SHORT_IMAGE_SINGLE_TEXT,
    "item-type-2": module.module_type === ModuleType.LONG_IMAGE_SINGLE_TEXT,
    "item-type-3": module.module_type === ModuleType.QUICK_ACCESS,
    "item-type-4":
      module.module_type === ModuleType.LEFT_IMAGE_RIGHT_DOUBLE_TEXT,
  };
};

// 处理文章点击
const handleArticleClick = (article: ArticleInfo) => {
  // 如果 jumpType 为 2，跳转到 page 路由
  if (article.jump_type === 2 && article.page_id) {
    router.push({
      path: "/page",
      query: {
        pageId: article.page_id.toString(),
      },
    });
    return;
  }

  // 其他情况保持原有逻辑
  if (article.article_url) {
    sdkBridge.open(article.article_url);
  }
};

// 处理图片加载错误
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.src =
    languageStore.getImage("iconEmpty") || getGameConfig().iconEmpty;
};
</script>

<style lang="scss" scoped>
.custom-modules-section {
  margin-bottom: 10px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 0 20px 16px;
  .custom-module {
    margin-bottom: 15px;
    background: transparent;
    border-radius: 0;
    overflow: hidden;
    &:last-child {
      margin-bottom: 0;
    }
  }
}

@media (min-width: 1024px) {
  .custom-modules-section {
    box-shadow: none;
    border-radius: 0;
  }
}

.module-header {
  .module-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 10px 0;
  }
}

.module-content {
  padding-bottom: 5px;
  // 布局类型1：偶数个项目的网格布局
  &.layout-type-1 {
    display: grid;
    gap: 14px 12px;
    grid-template-columns: repeat(2, 1fr);

    .article-item {
      min-width: 120px;
    }

    // 当只有1个项目时，占满一行
    .article-item:only-child {
      grid-column: 1 / -1;
    }
  }

  // 当type1有基数个项目时的特殊布局
  &.layout-type-1-odd {
    display: flex;
    flex-wrap: wrap;
    gap: 14px 12px;
    justify-content: space-evenly;

    .article-item {
      flex: 0 0 calc(50% - 6px);
      max-width: calc(50% - 6px);
    }

    // 基数个项目的最后一行（3个）居中显示
    .article-item:nth-last-child(3),
    .article-item:nth-last-child(2),
    .article-item:nth-last-child(1) {
      flex: 0 0 calc(33.333% - 8px);
      max-width: calc(33.333% - 8px);
    }

    // 当只有1个项目时，左对齐
    .article-item:only-child {
      flex: 0 0 calc(50% - 6px);
      max-width: calc(50% - 6px);
      margin-right: auto;
    }
  }

  // 布局类型2：每项占一行
  &.layout-type-2 {
    display: flex;
    flex-direction: column;
    gap: 14px 12px;
  }
}

.article-item {
  cursor: pointer;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.04);
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.25s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
  }

  // 公共图片样式
  .article-image {
    width: 100%;
    overflow: hidden;
    border-radius: 8px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  // 公共标题样式
  .article-title {
    padding: 8px 0 0;
    font-size: 12px;
    color: #666;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // 类型1：上图下文，固定比例 180:100
  &.item-type-1 {
    .article-image {
      aspect-ratio: 180 / 100;
    }
  }

  // 类型2：上图下文，固定比例 600:170
  &.item-type-2 {
    .article-image {
      aspect-ratio: 600 / 170;
    }
  }

  // 类型4：左图右双文案（小圆角背景，沿用公共背景样式）
  &.item-type-4 {
    display: flex;
    align-items: center;
    gap: 14px 12px;

    .article-image {
      flex-shrink: 0;
      width: 60px;
      height: 60px;
    }

    .article-text-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0; // 允许文本收缩

      .article-title,
      .article-subtitle {
        padding: 0;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .article-title {
        font-size: 12px;
        font-weight: 600;
        color: #333;
        -webkit-line-clamp: 2;
      }

      .article-subtitle {
        font-size: 10px;
        color: #666;
        -webkit-line-clamp: 1;
      }
    }
  }
}
</style>
